import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/field";
import { sessionQuery } from "@/features/auth/queries";
import { useSession } from "@/features/auth/useSession";
import { directoryQuery, nameFor } from "@/features/directory/queries";
import { masterTypeIndexQuery } from "@/features/master-types/queries";
import { deactivateRecord, reactivateRecord, submitRecord } from "@/features/records/api";
import { VersionHistory } from "@/features/records/components/VersionHistory";
import {
  ancestorsQuery,
  childrenQuery,
  recordEditQuery,
  recordVersionsQuery,
} from "@/features/records/queries";
import { activeSchemaQuery } from "@/features/schema/queries";
import { type FieldSpec, labelFor, parseDefinition } from "@/lib/forms/compile";

/**
 * One record: its values, where it sits in the hierarchy, what has
 * happened to it, and what this person may do to it next.
 *
 * Every action is gated by `can()` and every one of them is still refused
 * server-side — DEE-29's second criterion. Hiding a button is a courtesy;
 * the route re-checks, and the E2E suite (PR6) navigates straight to a
 * hidden one to prove it.
 */
export const Route = createFileRoute("/_authed/records/$code/$recordId/")({
  staticData: {
    crumbs: [
      { key: "nav.records", to: "/records" },
      { param: "code" },
      { key: "nav.breadcrumb.record" },
    ],
  },
  loader: async ({ context, params }) => {
    const me = await context.queryClient.ensureQueryData(sessionQuery);
    await Promise.all([
      context.queryClient.ensureQueryData(masterTypeIndexQuery(me.resolved_locale)),
      context.queryClient.ensureQueryData(activeSchemaQuery(params.code)),
      context.queryClient.ensureQueryData(recordEditQuery(params.code, params.recordId)),
      context.queryClient.ensureQueryData(directoryQuery()),
    ]);
  },
  component: RecordDetail,
});

function RecordDetail() {
  const { t } = useTranslation();
  const { code, recordId } = Route.useParams();
  const queryClient = useQueryClient();
  const { me, can } = useSession();
  const locale = me.resolved_locale;

  const { data: index } = useSuspenseQuery(masterTypeIndexQuery(locale));
  const { data: schema } = useSuspenseQuery(activeSchemaQuery(code));
  const { data: loaded } = useSuspenseQuery(recordEditQuery(code, recordId));
  const { data: directory } = useSuspenseQuery(directoryQuery());
  const record = loaded.data;

  const versions = useQuery(recordVersionsQuery(code, recordId));
  const ancestors = useQuery(ancestorsQuery(code, recordId, locale));
  const children = useQuery(childrenQuery(code, recordId, { locale, includeInactive: false }));

  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [cascade, setCascade] = useState(false);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["record-edit", code, recordId] });
    void queryClient.invalidateQueries({ queryKey: ["record-versions", code, recordId] });
    void queryClient.invalidateQueries({ queryKey: ["records", code] });
  };

  const submit = useMutation({
    mutationFn: () => submitRecord(code, recordId),
    onSuccess: () => {
      toast.success(t("records.detail.submitted"));
      invalidate();
    },
  });

  const deactivate = useMutation({
    mutationFn: () => deactivateRecord(code, recordId, cascade),
    onSuccess: () => {
      setConfirmDeactivate(false);
      toast.success(t("records.detail.submitted"));
      invalidate();
    },
  });

  const reactivate = useMutation({
    mutationFn: () => reactivateRecord(code, recordId),
    onSuccess: () => {
      toast.success(t("records.detail.submitted"));
      invalidate();
    },
  });

  const fields = (() => {
    if (schema === null) return [];
    try {
      return parseDefinition(schema.definition);
    } catch {
      return [];
    }
  })();

  const isDraft = record.status === "draft";
  const isApprovedCurrent = record.status === "approved" && record.is_current;

  return (
    <>
      <PageHeader
        title={
          record.display_name[locale] ??
          record.display_name[me.tenant.default_locale] ??
          record.business_key
        }
        description={record.business_key}
        actions={
          <div className="flex flex-wrap gap-2">
            {can("record.edit", code) && (isDraft || isApprovedCurrent) && (
              <Button
                asChild
                variant="secondary"
                tooltip={isDraft ? t("tooltips.editRecord") : t("tooltips.amendRecord")}
              >
                <Link to="/records/$code/$recordId/edit" params={{ code, recordId }}>
                  {isDraft ? t("records.detail.edit") : t("records.detail.amend")}
                </Link>
              </Button>
            )}
            {can("record.submit", code) && isDraft && (
              <Button
                onClick={() => submit.mutate()}
                disabled={submit.isPending}
                tooltip={t("tooltips.submitForReview")}
              >
                {t("records.detail.submit")}
              </Button>
            )}
            {can("record.deactivate", code) &&
              isApprovedCurrent &&
              (record.is_active ? (
                <Button
                  variant="danger"
                  onClick={() => setConfirmDeactivate(true)}
                  tooltip={t("tooltips.deactivateRecord")}
                >
                  {t("records.detail.deactivate")}
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  onClick={() => reactivate.mutate()}
                  disabled={reactivate.isPending}
                  tooltip={t("tooltips.reactivateRecord")}
                >
                  {t("records.detail.reactivate")}
                </Button>
              ))}
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <StatusBadge
          status={
            record.status === "approved" && !record.is_active
              ? "inactive"
              : (record.status as "draft" | "pending" | "approved" | "rejected")
          }
        />
        <span className="text-sm text-muted">
          {t("records.detail.version", { version: record.version })}
        </span>
      </div>

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <section aria-labelledby="fields-heading" className="space-y-3">
          <h2 id="fields-heading" className="text-sm font-semibold text-foreground">
            {t("records.detail.fields")}
          </h2>
          <dl className="divide-y divide-border rounded-panel border border-border bg-surface">
            {fields.map((field) => (
              <div key={field.name} className="grid gap-1 p-3 sm:grid-cols-3">
                <dt className="text-sm text-muted">{labelFor(field)}</dt>
                <dd className="text-sm text-foreground sm:col-span-2">
                  {renderValue(
                    record.payload[field.name],
                    field,
                    locale,
                    me.tenant.default_locale,
                  ) ?? t("records.detail.noValue")}
                </dd>
              </div>
            ))}
            {fields.length === 0 && (
              <p className="p-3 text-sm text-muted">{t("records.noSchema.title")}</p>
            )}
          </dl>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
            <h2 className="text-sm font-semibold text-foreground">{t("records.detail.history")}</h2>
            {can("audit.read.master_data", code) && (
              <Link
                to="/audit/master-data"
                search={{ entityType: "master_record", entityId: recordId }}
                className="text-sm text-primary hover:underline"
              >
                {t("records.detail.viewAuditTrail")}
              </Link>
            )}
          </div>
          <VersionHistory
            versions={versions.data?.items ?? []}
            // Names, as of PR6 — `GET /api/v1/directory/users` is the
            // narrow, reviewer-readable lookup DEE-29 said this was
            // waiting on. An id that resolves to nobody still renders as
            // the id: a colleague removed from the tenant is exactly who
            // an admin would go searching the audit log for.
            userLabel={(userId) => nameFor(directory, userId) ?? t("records.detail.notApproved")}
          />
        </section>

        <aside aria-labelledby="hierarchy-heading" className="space-y-3">
          <h2 id="hierarchy-heading" className="text-sm font-semibold text-foreground">
            {t("records.detail.hierarchy")}
          </h2>
          <nav aria-label={t("records.detail.hierarchy")}>
            <ol className="space-y-1 text-sm">
              {(ancestors.data ?? []).map((node) => {
                const nodeCode = index.byId.get(node.master_type_id)?.code;
                if (nodeCode === undefined) return null;
                return (
                  <li key={node.id}>
                    <Link
                      to="/records/$code/$recordId"
                      params={{ code: nodeCode, recordId: node.id }}
                      className="text-primary hover:underline"
                    >
                      {node.display_name ?? node.business_key}
                    </Link>
                  </li>
                );
              })}
            </ol>
          </nav>

          <h3 className="pt-2 text-sm font-semibold text-foreground">
            {t("records.detail.children")}
          </h3>
          {(children.data ?? []).length === 0 ? (
            <p className="text-sm text-muted">{t("records.detail.noChildren")}</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {(children.data ?? []).map((node) => {
                const nodeCode = index.byId.get(node.master_type_id)?.code;
                if (nodeCode === undefined) return null;
                return (
                  <li key={node.id}>
                    <Link
                      to="/records/$code/$recordId"
                      params={{ code: nodeCode, recordId: node.id }}
                      className="text-primary hover:underline"
                    >
                      {node.display_name ?? node.business_key}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>
      </div>

      <ConfirmDialog
        open={confirmDeactivate}
        onOpenChange={setConfirmDeactivate}
        title={t("records.detail.confirmDeactivate.title")}
        description={t("records.detail.confirmDeactivate.body")}
        confirmLabel={t("records.detail.deactivate")}
        cancelLabel={t("common.cancel")}
        destructive
        onConfirm={() => deactivate.mutate()}
      >
        <Checkbox
          checked={cascade}
          onChange={(event) => setCascade(event.target.checked)}
          label={t("records.detail.cascade")}
          tooltip={t("tooltips.cascade")}
        />
      </ConfirmDialog>
    </>
  );
}

/**
 * A stored value as text.
 *
 * Localised fields arrive as `{locale: value}` from the edit view, which
 * is what lets this page show the reader's own language without a request
 * per field.
 *
 * Instants are stored and served as UTC and rendered here in the reader's
 * timezone — §16.1 #6 — so a `datetime` field shows a time somebody
 * recognises rather than `2026-08-09T05:00:00.000Z`. The *field's declared
 * type* decides that, not the shape of the value: a `string` field holding
 * something date-shaped is a string, and reformatting it would be this
 * page inventing a meaning the schema never gave it.
 */
function renderValue(
  value: unknown,
  field: FieldSpec,
  locale: string,
  defaultLocale: string,
): string | undefined {
  if (value === undefined || value === null || value === "") return undefined;

  if (field.localised && typeof value === "object" && !Array.isArray(value)) {
    const map = value as Record<string, unknown>;
    const picked = map[locale] ?? map[defaultLocale] ?? Object.values(map)[0];
    return picked === undefined || picked === "" ? undefined : String(picked);
  }

  if (typeof value === "string" && (field.type === "date" || field.type === "datetime")) {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return field.type === "date" ? parsed.toLocaleDateString() : parsed.toLocaleString();
  }

  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}
